import type { Ficha as FichaDoContrato } from "../api"
import { Column } from "../componentes/TablePrincipal2"
import { Selo } from "../componentes/Selo"
import { data, moeda, percentual, rotulo } from "../formato"
import { MAIUSCULA } from "../lib/classes"
import { BlocoEditavel } from "./Campos"
import { COLUNA_NUMERICA, DetalheDaFicha, TabelaDaFicha, VazioDaFicha, useLinhasAbertas } from "./Tabela"

type Aditivo = FichaDoContrato["aditivos"][number]
type Apostilamento = FichaDoContrato["apostilamentos"][number]

const NUMERO = "tabular-nums"
const VALOR = "block text-center tabular-nums"

export function BlocoAditivos({ ficha }: { ficha: FichaDoContrato }) {
  const linhas = useLinhasAbertas<number>()
  return (
    <BlocoEditavel
      titulo="Aditivos"
      editavel={false}
      recado="Alteram o ajuste. Acréscimos entram no limite do art. 125."
      leitura={
        <TabelaDaFicha<Aditivo>
          dados={ficha.aditivos}
          ordenar={{ ordem: (a) => a.ordem, data: (a) => a.data, valor: (a) => Number(a.valor) }}
          vazio={<VazioDaFicha titulo="Nenhum termo aditivo" />}
          linhaClicavel={(a) => a.justificativa !== null}
          aoClicarNaLinha={(a) => linhas.alternar(a.id)}
          linhaAberta={(a) => linhas.aberta(a.id)}
          detalheDaLinha={(a) => <DetalheDaFicha rotulo="Justificativa">{a.justificativa}</DetalheDaFicha>}
        >
          <Column<Aditivo> name="ordem" label="Aditivo" render={(_v, a) => <span className={NUMERO}>{`${a.ordem}º`}</span>} />
          <Column<Aditivo>
            name="tipo"
            label="Tipo"
            render={(_v, a) => (
              <span className="flex flex-col">
                <span className={MAIUSCULA}>{rotulo(a.tipo)}</span>
                <span className="text-xs text-muted-foreground">{a.unilateral ? "Unilateral" : "Por acordo"}</span>
              </span>
            )}
          />
          <Column<Aditivo> name="data" label="Data" render={(_v, a) => <span className={NUMERO}>{data(a.data)}</span>} />
          <Column<Aditivo> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{moeda(a.valor)}</span>} />
          <Column<Aditivo> name="percentual" label="%" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{percentual(a.percentual)}</span>} />
          <Column<Aditivo> name="percentual_acumulado" label="Acumulado" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{percentual(a.percentual_acumulado)}</span>} />
          <Column<Aditivo> name="limite_aplicado" label="Limite" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{percentual(a.limite_aplicado)}</span>} />
          <Column<Aditivo>
            name="excedeu_o_limite"
            label="Situação"
            render={(_v, a) => <Selo situacao={a.excedeu_o_limite ? "acima_do_limite" : "dentro_do_limite"} />}
          />
        </TabelaDaFicha>
      }
    />
  )
}

export function BlocoApostilamentos({
  linhas: dados,
  titulo,
  vazio,
  nota,
}: {
  linhas: Apostilamento[]
  titulo: string
  vazio: string
  nota: string
}) {
  const linhas = useLinhasAbertas<number>()
  return (
    <BlocoEditavel
      titulo={titulo}
      editavel={false}
      recado={nota}
      leitura={
        <TabelaDaFicha<Apostilamento>
          dados={dados}
          ordenar={{ ordem: (a) => a.ordem, data: (a) => a.data, valor: (a) => Number(a.valor) }}
          vazio={<VazioDaFicha titulo={vazio} />}
          linhaClicavel={(a) => a.justificativa !== null}
          aoClicarNaLinha={(a) => linhas.alternar(a.id)}
          linhaAberta={(a) => linhas.aberta(a.id)}
          detalheDaLinha={(a) => <DetalheDaFicha rotulo="Justificativa">{a.justificativa}</DetalheDaFicha>}
        >
          <Column<Apostilamento> name="ordem" label="Apostilamento" render={(_v, a) => <span className={NUMERO}>{`${a.ordem}º`}</span>} />
          <Column<Apostilamento> name="tipo" label="Tipo" render={(_v, a) => <span className={MAIUSCULA}>{rotulo(a.tipo)}</span>} />
          <Column<Apostilamento> name="data" label="Data" render={(_v, a) => <span className={NUMERO}>{data(a.data)}</span>} />
          <Column<Apostilamento> name="indice" label="Índice" render={(_v, a) => <span className={MAIUSCULA}>{a.indice ?? "—"}</span>} />
          <Column<Apostilamento> name="percentual" label="%" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{percentual(a.percentual)}</span>} />
          <Column<Apostilamento> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, a) => <span className={VALOR}>{moeda(a.valor)}</span>} />
        </TabelaDaFicha>
      }
    />
  )
}
